import { Box } from '@chronosphereio/chrono-ui';
import { formatMsDuration, getRangeMs } from '@chronosphereio/core';
import type {
  EChartsCoreOption,
  GridComponentOption,
  LineSeriesOption,
  TooltipComponentOption,
  XAXisComponentOption,
} from 'echarts';
import {
  BarChart as EChartsBarChart,
  LineChart as EChartsLineChart,
  ScatterChart as EChartsScatterChart,
} from 'echarts/charts';
import {
  DatasetComponent,
  DataZoomComponent,
  GridComponent,
  MarkAreaComponent,
  MarkLineComponent,
  MarkPointComponent,
  TitleComponent,
  ToolboxComponent,
  TooltipComponent,
} from 'echarts/components';
import { use } from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { merge } from 'lodash';
import {
  forwardRef,
  MouseEvent as ReactMouseEvent,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  CursorCoordinates,
  TimeChartTooltip,
  usePanelTooltipActions,
  usePanelTooltipPortalContainer,
  useTooltipContext,
} from '@/components';
import {
  ContentDimensions,
  EVENT_SERIES_NAME,
  EventItem,
  OPTIMIZED_MODE_TIME_SERIES_LIMIT,
  TimeChartSeriesMapping,
  TimeScale,
  TimeSeriesDataset,
  UnitOptions,
} from '@/core';
import { useTimeRange } from '@/plugin-system/runtime/TimeRangeProviderV2';
import { useUserTimeFormat } from '@/plugin-system/runtime/useUserTimeFormat';
import { useChartsContext } from '../../../components/context/ChartsProvider';
import { useTimeZone } from '../../../components/context/TimeZoneProvider';
import { ChartApi, EChart, MouseEventsParameters } from '../../../components/EChart';
import { ChartInstance, ChartInstanceFocusOpts, DEFAULT_PINNED_CROSSHAIR } from '../../../components/model';
import {
  checkDatasetForClosestTimestamp,
  clearHighlightedSeries,
  enableDataZoom,
  getFormattedAxis,
  getFormattedTimeAxisLabel,
  getPointInGrid,
  restoreChart,
  ZoomEventData,
} from '../../../components/utils';
import { useThrottledResizing } from '../../../core/utils/resizing';
import { useIsOnScreen } from '../../../plugin-system/runtime/in-view';
import { useTimeChartPinning, useVisualSettings } from './hooks';
import { TimeSeriesChartExtendedYAxisOptions, TimeSeriesChartYAxisComponentOption } from './time-series-chart-model';
import { calculateNoDataYAxis } from './utils/data-transform';
import { getXAxisMinInterval, useComputeEventSeries } from './utils/model';
import { getTooltipOption } from './utils/tooltip-config';

// eslint-disable-next-line react-hooks/rules-of-hooks
use([
  EChartsLineChart,
  EChartsBarChart,
  EChartsScatterChart,
  GridComponent,
  DatasetComponent,
  DataZoomComponent,
  MarkAreaComponent,
  MarkLineComponent,
  MarkPointComponent,
  TitleComponent,
  ToolboxComponent,
  TooltipComponent,
  CanvasRenderer,
]);

export interface TimeChartBaseProps extends ContentDimensions {
  dataset: TimeSeriesDataset[];
  events?: EventItem[];
  seriesMapping: TimeChartSeriesMapping;
  timeScale: TimeScale;
  yAxes: TimeSeriesChartExtendedYAxisOptions[];
  unit?: UnitOptions;
  grid?: GridComponentOption;
  syncGroup?: string;
  isStacked?: boolean;
  onDataZoom?: (e: ZoomEventData) => void;
  onClick?: (e: MouseEventsParameters<unknown>) => void;
  onDoubleClick?: (e: ReactMouseEvent) => void;

  /**
   * Pre-resolved x-axis bounds from the panel snapshot layer.
   * When provided, these take priority over internally derived bounds from `useTimeRange`.
   * This enables the panel layer to stabilize x-axis bounds during refetch to prevent
   * the chart from flashing with a mismatched range before new data settles.
   */
  xAxisMinMs?: number;
  xAxisMaxMs?: number;
  xAxisRangeMs?: number;
}

// Values for the echart `option` that do not change. Defining them once outside the render
// cycle to ensure they are really constant, and thus do not unnecessarily trigger
// option updates, which can contribute to performance issues.
const CONSTANT_OPTIONS: EChartsCoreOption = {
  // https://echarts.apache.org/en/option.html#axisPointer
  axisPointer: {
    type: 'line',
    z: 0, // ensure point symbol shows on top of dashed line
    triggerEmphasis: false, // https://github.com/apache/echarts/issues/18495
    triggerTooltip: false,
    snap: false, // xAxis.axisPointer.snap takes priority
  },
  toolbox: {
    feature: {
      dataZoom: {
        icon: null, // https://stackoverflow.com/a/67684076/17575201
        yAxisIndex: 'none',
      },
    },
  },
};

/**
 * This component is deprecated and no longer exported for external use. Use the Time Series Chart instead.
 *
 * @deprecated
 */
export const TimeChartBase = forwardRef<ChartInstance, TimeChartBaseProps>(function TimeChartBase(
  {
    width,
    height,
    dataset,
    seriesMapping,
    timeScale,
    yAxes,
    unit,
    grid,
    isStacked = false,
    syncGroup,
    onDataZoom,
    onClick,
    onDoubleClick,
    events = [],
    xAxisMinMs,
    xAxisMaxMs,
    xAxisRangeMs,
  },
  ref
) {
  const { chartsTheme, enablePinning } = useChartsContext();
  const tooltipConfig = useTooltipContext();
  const isPinningEnabled = tooltipConfig.enablePinning && enablePinning;

  const [timeChartBaseContainerEl, setTimeChartBaseContainerEl] = useState<HTMLDivElement | null>(null);
  // Get container element to attach tooltip portal to.
  const { tooltipPortalContainerEl: containerElement } = usePanelTooltipPortalContainer({
    rootEl: timeChartBaseContainerEl,
  });

  const visual = useVisualSettings();

  // Ref to an imperative api for interacting with ECharts.
  const chartApiRef = useRef<ChartApi>();

  const { setActivePanelKey } = usePanelTooltipActions();

  const [showTooltip, setShowTooltip] = useState<boolean>(true);
  const [mouseoverSeriesId, setMouseoverSeriesId] = useState<string>();
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const { absoluteTimeRange } = useTimeRange();
  const liveRangeMs = getRangeMs(absoluteTimeRange);
  const liveStartTimeMs = absoluteTimeRange.start.valueOf();
  const liveEndTimeMs = absoluteTimeRange.end.valueOf();

  // Use externally resolved bounds when provided (stabilized by panel snapshot),
  // falling back to live time-range context values.
  const startTimeMs = xAxisMinMs ?? liveStartTimeMs;
  const endTimeMs = xAxisMaxMs ?? liveEndTimeMs;
  const rangeMs = xAxisRangeMs ?? liveRangeMs;

  const { timeZone } = useTimeZone();
  const timeFormat = useUserTimeFormat();

  const totalSeries = dataset?.length ?? 0;

  const { throttledWidth } = useThrottledResizing(
    { width, height },
    {
      // We use a moderate throttle for this because it primarily impacts the time-based
      // x-axis reorganizing, which isn't pixel-perfect, so you don't notice choppiness
      // if it runs a little slower.
      throttleDimensionsMs: 100,
    }
  );

  useImperativeHandle(ref, () => {
    return {
      highlightSeries({ name }: ChartInstanceFocusOpts) {
        if (!chartApiRef.current) {
          // when chart undef, do not highlight series when hovering over legend
          return;
        }

        chartApiRef.current.dispatchAction({ type: 'highlight', seriesId: name });
      },
      clearHighlightedSeries: () => {
        if (!chartApiRef.current) {
          // when chart undef, do not clear highlight series
          return;
        }
        clearHighlightedSeries(chartApiRef.current);
      },
    };
  }, []);

  const { clearPinned, tooltipPinnedCoords, setTooltipPinnedCoords, pinnedCrosshair, setPinnedCrosshair } =
    useTimeChartPinning({
      chartApiRef: chartApiRef,
    });

  useEffect(() => {
    // Reset pinning when data changes because the pinning may no longer be
    // valid.
    clearPinned();
  }, [clearPinned, dataset]);

  const handleMouseover = useCallback(
    (params: MouseEventsParameters<LineSeriesOption['data'] | unknown>) => {
      if (tooltipPinnedCoords === null) {
        // Skip mouseover when the target element is a polygon because that is only triggered for the area portion below
        // the line when area is enabled. We only want to trigger this logic on mouseover of the actual line, which is
        // a different shape. Stacked series are an exception since they will never overlap.
        // TODO: move to triggerEvent approach in future ECharts release
        if (isStacked || params.event?.target?.type !== 'ec-polygon') {
          // Used in tooltip bold series name logic, seriesName is actually the id from getTimeSeries.
          // Workaround is necessary since seriesId is not returned in the params here.
          // Note: triggerLineEvent must be true in getTimeSeries for the mouseover event to work correctly.
          setMouseoverSeriesId(params.seriesName);
        }
      }
    },
    [tooltipPinnedCoords, isStacked]
  );

  const handleMouseout = useCallback(() => {
    if (tooltipPinnedCoords === null) {
      // Reset bold series showing in tooltip when not hovering directly over a line.
      setMouseoverSeriesId(undefined);
    }
  }, [tooltipPinnedCoords]);

  const handleDatazoom = useCallback(
    (params: { batch: Array<{ startValue?: number; endValue?: number }> }) => {
      if (onDataZoom === undefined) {
        setTimeout(() => {
          // workaround so unpin happens after click event
          setTooltipPinnedCoords(null);
        }, 10);
      }
      if (onDataZoom === undefined || params.batch[0] === undefined) return;
      const xAxisStartValue = params.batch[0].startValue;
      const xAxisEndValue = params.batch[0].endValue;
      if (xAxisStartValue !== undefined && xAxisEndValue !== undefined) {
        const zoomEvent: ZoomEventData = {
          start: xAxisStartValue,
          end: xAxisEndValue,
        };
        onDataZoom(zoomEvent);
      }
    },
    [onDataZoom, setTooltipPinnedCoords]
  );

  const handleFinished = useCallback(() => {
    if (chartApiRef.current !== undefined) {
      enableDataZoom(chartApiRef.current);
    }
  }, []);

  const { noDataOption } = chartsTheme;

  const eventSeries = useComputeEventSeries({
    events,
    startTime: startTimeMs,
  });

  // Used for tooltip event series lookup
  const eventSeriesOption = useMemo(() => {
    if (!eventSeries) return undefined;
    return eventSeries.find((series) => series.name === EVENT_SERIES_NAME);
  }, [eventSeries]);

  // Add event series to end of seriesMapping array so events are plotted
  const seriesMappingWithEvents = useMemo(() => {
    if (eventSeries) {
      return seriesMapping.concat(eventSeries);
    }

    return seriesMapping;
  }, [seriesMapping, eventSeries]);

  const minInterval = useMemo(() => {
    // Adjust x-axis minInterval based on chart width so labels are not squished together
    // https://echarts.apache.org/en/tutorial.html#Responsive%20Mobile-End
    return getXAxisMinInterval(throttledWidth, rangeMs);
  }, [rangeMs, throttledWidth]);

  const tooltip = useMemo(() => {
    return getTooltipOption();
  }, []);

  const xAxis: XAXisComponentOption = useMemo(() => {
    const isBarDisplay = visual.display === 'bar';
    // TODO: Figure out how to get the step size while also allowing data plugins to bypass getTimeSeriesValue processing
    // https://app.shortcut.com/chronosphere/story/126039
    const isStepMsDefined = typeof timeScale.stepMs === 'number' && timeScale.stepMs > 0;
    const showStepLabel = isBarDisplay && isStepMsDefined;
    return {
      type: 'time',
      min: startTimeMs,
      max: endTimeMs,
      minInterval,
      axisLabel: {
        hideOverlap: true,
        formatter: (value) => {
          const timezoneFormattedValue = getFormattedTimeAxisLabel({
            value,
            rangeMs,
            timeFormat,
            timeZone,
          });
          return timezoneFormattedValue;
        },
      },
      axisPointer: {
        snap: false, // important so shared crosshair does not lag
        label: {
          show: showStepLabel,
          // https://echarts.apache.org/en/option.html#xAxis.axisPointer.label.formatter
          formatter: timeScale.stepMs ? formatMsDuration(timeScale.stepMs) : '',
        },
      },
    };
  }, [visual.display, timeScale, startTimeMs, endTimeMs, minInterval, rangeMs, timeFormat, timeZone]);

  const yAxisOption: TimeSeriesChartYAxisComponentOption[] = useMemo(() => {
    // when events are present increase padding above time series data so tooltip less likely to clash
    const eventsBoundaryOffset = events.length ? '50%' : '10%'; // TODO: play around with first value since ideal value depends on data

    const yAxisLeft = yAxes[0];

    // Alternate y axis is hidden unless it's explicitly set and shows to the right.
    const yAxisRight = yAxes[1];

    return [
      ...getFormattedAxis({ ...yAxisLeft, boundaryGap: [0, eventsBoundaryOffset] }, unit),
      ...getFormattedAxis({ ...yAxisRight, boundaryGap: [0, eventsBoundaryOffset] }, yAxisRight?.unit ?? unit),
      // a third yaxis to support events
      {
        zlevel: 1,
        z: 1,
        show: false,
        type: 'value',
        axisLabel: {
          show: false,
        },
        axisLine: {
          show: false,
        },
        min: 0,
        max: 1, // ensure event icon is always at the max
      },
    ] as TimeSeriesChartYAxisComponentOption[];
  }, [events.length, unit, yAxes]);

  const option: EChartsCoreOption = useMemo(() => {
    const noData = dataset.length === 0;

    if (dataset === null) return noDataOption;

    let updatedSeriesMapping =
      enablePinning && pinnedCrosshair !== null
        ? [...seriesMappingWithEvents, pinnedCrosshair]
        : seriesMappingWithEvents;

    // Add event series to the end of the series mapping
    if (eventSeries !== null) {
      updatedSeriesMapping = [...updatedSeriesMapping, ...eventSeries];
    }

    const noSeriesShown = updatedSeriesMapping.length === 0;
    const showEmptyChart = noData || noSeriesShown;
    // Show an empty state message with the empty chart if the data is empty. We do not show the message when series
    // are empty because that usually means the user deselected all items in the legend (not that there's no data).
    const showEmptyMessage = noData;

    const updatedYAxisOption = !showEmptyChart
      ? // When data is not empty,leave the yAxis config as is.
        yAxisOption
      : // When data is empty, adjust the yAxis config to show an empty yAxis with just 0.
        calculateNoDataYAxis(yAxisOption);

    // When showing an empty message, extend from the "no data" options, which include the config
    // for showing the message.
    const baseOptions = showEmptyMessage ? noDataOption : {};

    const option: EChartsCoreOption = {
      ...baseOptions,
      ...CONSTANT_OPTIONS,
      dataset: dataset,
      series: updatedSeriesMapping,
      xAxis,
      yAxis: updatedYAxisOption,
      animation: false,
      tooltip,
      grid,
    };

    return option;
  }, [
    seriesMappingWithEvents,
    grid,
    noDataOption,
    enablePinning,
    pinnedCrosshair,
    eventSeries,
    dataset,
    tooltip,
    xAxis,
    yAxisOption,
  ]);

  // Unmount the tooltip when chart is offscreen.
  // Leave true when undefined since InViewContext is optional for teams using
  // lower level components.
  const onScreenCtx = useIsOnScreen();
  const isOnScreen = onScreenCtx === undefined ? true : onScreenCtx.isInView;

  return (
    <Box
      sx={{ width, height }}
      ref={setTimeChartBaseContainerEl}
      // onContextMenu={(e) => {
      //   // TODO: confirm tooltip pinning works correctly on Windows, should e.preventDefault() be added here
      //   e.preventDefault(); // Prevent the default behaviour when right clicked
      // }}
      onClick={(e) => {
        // Allows user to opt-in to multi tooltip pinning when Ctrl or Cmd key held down
        const isControlKeyPressed = e.ctrlKey || e.metaKey;
        if (isControlKeyPressed) {
          e.preventDefault();
        }

        // Determine where on chart canvas to plot pinned crosshair as markLine.
        const pointInGrid = getPointInGrid(e.nativeEvent.offsetX, e.nativeEvent.offsetY, chartApiRef.current);
        if (!pointInGrid) {
          return;
        }

        // Pin and unpin when clicking on chart canvas but not tooltip text.
        if (isPinningEnabled && e.target instanceof HTMLCanvasElement) {
          // Pin tooltip and update shared charts context to remember these coordinates.
          const pinnedPos: CursorCoordinates = {
            page: {
              x: e.pageX,
              y: e.pageY,
            },
            client: {
              x: e.clientX,
              y: e.clientY,
            },
            plotCanvas: {
              x: e.nativeEvent.offsetX,
              y: e.nativeEvent.offsetY,
            },
            target: e.target,
          };

          setTooltipPinnedCoords((current) => {
            if (current === null) {
              return pinnedPos;
            } else {
              setPinnedCrosshair(null);
              return null;
            }
          });

          // Only pin crosshair if click happens inside the grid
          // This is to prevent crosshair from pinning when you click on the event icon
          if (chartApiRef.current?.containPixel('grid', [e.nativeEvent.offsetX, e.nativeEvent.offsetY])) {
            setPinnedCrosshair((current) => {
              // Only add pinned crosshair line series when there is not one already in seriesMapping.
              if (current === null) {
                const { closestTimestamp } = checkDatasetForClosestTimestamp(dataset, pointInGrid, timeScale);

                // Crosshair snaps to nearest timestamp since cursor may be slightly to left or right
                if (closestTimestamp !== null) {
                  const pinnedCrosshair = merge({}, DEFAULT_PINNED_CROSSHAIR, {
                    markLine: {
                      label: {
                        show: false,
                      },
                      data: [
                        {
                          xAxis: closestTimestamp,
                        },
                      ],
                    },
                    tooltip: {
                      trigger: 'none',
                    },
                  } as LineSeriesOption);
                  return pinnedCrosshair;
                }

                return null;
              } else {
                // Clear previously set pinned crosshair
                return null;
              }
            });
          }
        }
      }}
      onMouseDown={(e) => {
        const { clientX } = e;
        setIsDragging(true);
        setStartX(clientX);
      }}
      onMouseMove={(e) => {
        // Allow clicking inside tooltip to copy labels.
        if (!(e.target instanceof HTMLCanvasElement)) {
          return;
        }
        const { clientX } = e;
        if (isDragging) {
          const deltaX = clientX - startX;
          if (deltaX > 0) {
            // Hide tooltip when user drags to zoom.
            setShowTooltip(false);
          }
        }
      }}
      onMouseUp={() => {
        setIsDragging(false);
        setStartX(0);
        setShowTooltip(true);
      }}
      onMouseLeave={() => {
        setActivePanelKey(undefined);
        if (chartApiRef.current !== undefined) {
          clearHighlightedSeries(chartApiRef.current);
        }
      }}
      onMouseEnter={() => {
        setActivePanelKey(tooltipConfig.panelKey);
        setShowTooltip(true);
        if (chartApiRef.current !== undefined) {
          enableDataZoom(chartApiRef.current);
        }
      }}
      onDoubleClick={(e) => {
        setTooltipPinnedCoords(null);
        // either dispatch ECharts restore action to return to orig state or allow consumer to define behavior
        if (onDoubleClick === undefined) {
          if (chartApiRef.current !== undefined) {
            restoreChart(chartApiRef.current);
          }
        } else {
          onDoubleClick(e);
        }
      }}
    >
      {/* Allows overrides prop to hide custom tooltip and use the ECharts option.tooltip instead */}
      {showTooltip === true &&
        isOnScreen &&
        (option.tooltip as TooltipComponentOption)?.showContent === false &&
        tooltipConfig.hidden !== true && (
          <TimeChartTooltip
            tooltipConfig={tooltipConfig}
            containerElement={containerElement}
            chartApiRef={chartApiRef}
            dataset={dataset}
            seriesMapping={seriesMappingWithEvents}
            timeRange={{ start: new Date(startTimeMs), end: new Date(endTimeMs) }}
            timeScale={timeScale}
            mouseoverSeriesId={mouseoverSeriesId}
            enablePinning={isPinningEnabled}
            pinnedPos={tooltipPinnedCoords ?? undefined}
            unit={unit}
            isStacked={isStacked}
            eventSeriesOption={eventSeriesOption}
            onUnpinClick={clearPinned}
          />
        )}
      <EChart
        width={width}
        height={height}
        option={option}
        theme={chartsTheme.echartsTheme}
        onMouseover={handleMouseover}
        onMouseout={handleMouseout}
        onDataZoom={handleDatazoom}
        onFinished={handleFinished}
        onClick={onClick}
        chartApiRef={chartApiRef}
        syncGroup={totalSeries >= OPTIMIZED_MODE_TIME_SERIES_LIMIT ? undefined : syncGroup}
      />
    </Box>
  );
});
